import { fn } from '../utils';
import resolveToError = fn.resolveToError;
import { Observable } from "../nav";
import { IRwfSettings } from "./rwf.backend";
import { IRoutingService } from "../services/autoresolveservice";
import { CompatibilityService } from "../services/compatibilityservice";
import { RwfSignalRService } from "../rwfsignalr";
import { IHubChangeInfo, IKWFConnectionInfo, IKWFServerState, IKWFHub, IKWFClientState, INavUiPageInfo } from '../rwfsignalrtypes';
import { RwfAppSettings } from "./rwf.appsettings";
import { NavClientService, INavUrlInfo, TNavUrlSource, ResolveNavUrl } from "../navdata";
import { AppVersion } from "../interfaces";
import { IRwfBootstrapParameters } from "./rwf.bootsrap";
import { RwfInvoiceDataService } from './rwfinvoicedata';
import { IDialogService } from '@common/services/DialogService';

/** Root KWF Application Service */
export abstract class RwfApplicationService extends Observable {
  constructor(
    public readonly $c: CompatibilityService,
    public readonly appVersion: AppVersion) {
    super();
    this.NavClient = $c.resolve("NavClient");
    const routing = this.routing = $c.resolve("Routing");
    this.DialogService = $c.resolve("DialogService");
    this.RwfSignalRService = $c.resolve("RwfSignalR");
    const rwf = $c.resolve("Rwf");
    this.RwfAppSettings = rwf("RwfAppSettings");

    const rwfBootstrap = this.$c.resolve("Rwf")("RwfBootstrap");
    this.rwfBootstrap = rwfBootstrap.Resolve;
    rwfBootstrap.OnResolved((boot: IRwfBootstrapParameters) => this.LoadedSettings = boot.RwfSettings);

    this.NavUrl = this.rwfBootstrap.then(s => s.NavUrlInfo);

    // Reset edit change registration when SignalR disconnects
    this.RwfSignalRService.ConnectionFailure.subscribe(isDisconnected => {
      if (isDisconnected) {
        console.log("SignalR disconnected - resetting edit change registration");
        this.isEditChangeRegistered = false;
      } else {
        console.log("SignalR reconnected - will re-register edit notifications");
        this.reRegisterEditNotifications();
      }
    });

    const appState = this._appState = {
      StartUpTime: new Date(),
      LastRouteChange: undefined,
      CurrentUrl: routing.CurrentUrl,
      Version: appVersion,
      UserAgent: window.navigator.userAgent
    };
    routing.CurrentRoute.subscribe(() => appState.LastRouteChange = new Date());

    let resolved = $c.defer<RwfApplicationService>();
    this.Resolved = resolved.promise;

    this.EnsureResolved = () => {
      if (!resolve) {
        // Handle "External Resolution" Scenario
        const navUrl = this.NavClient.NavUrl;
        const isExternallyInitialized = navUrl.ExternalDefaultUrl && (navUrl.ExternalDefaultUrl !== this);
        if (isExternallyInitialized) {
          // Propagate resolution to Own handling
          return resolve = navUrl.NavUrl.then(() => this);
        }

        var resolve = this.GetGlobalSettings().then(() => this);
        resolve.then(() => { resolved.resolve(this); this.getKwfHub(true); }, resolved.reject);
      }
      return resolve;
    };

    // this.Resolve = as imp Create
    // Object.defineProperty(this, "Resolve", { get: this.EnsureResolved });
  }

  public readonly RwfAppSettings: RwfAppSettings;
  public readonly NavClient: NavClientService;
  public readonly routing: IRoutingService;
  public readonly DialogService: IDialogService;
  public readonly RwfSignalRService: RwfSignalRService;

  /**Promise to Retrieve Global Settings: Determines Auth Context -> First Call! */
  GetGlobalSettings(): PromiseLike<IRwfSettings> {
    return this.rwfBootstrap.then(s => s.RwfSettings);
  }

  private readonly rwfBootstrap: PromiseLike<IRwfBootstrapParameters>;
  // MSQQQ
  public readonly NavUrl: PromiseLike<INavUrlInfo>;

  LoadAdminSettings() {
    return this.$c.get<{ Name: string, Value: string }>("infoma.Web.KWF/AdminSettings").then(d => d.data);
  }

  /** Get Invoice Data Service : Either Sharepoint or NAV bound */
  GetInvoiceData(impCreate = true): PromiseLike<RwfInvoiceDataService> {
    if (!this.getInvoiceData && impCreate) {
      this.getInvoiceData = this.$c.when().then(() => {
        return this.$c.resolve("Rwf")("RwfInvoiceData");
      }).then(service => service.Resolve.then(() => service));
    };

    return this.getInvoiceData;
  }
  private getInvoiceData: PromiseLike<RwfInvoiceDataService>;

  /** Ensure Service is Resolved */
  public EnsureResolved: () => PromiseLike<RwfApplicationService>;

  /** Promise to be resolved upon Resolution of Service (does not trigger Resolution) */
  public Resolved: PromiseLike<RwfApplicationService>;

  /** Loaded Settings, Accessible only after Resolve! */
  public LoadedSettings: IRwfSettings;

  /** Start Hub Notification about Start/End Edit */
  public NotifyEditChange(info: IHubChangeInfo[], navUrl: string) {
    return this.getKwfHub(false).then(hub => {
      return this.$c.when(hub.server.editChange(info, navUrl));
    });
  }

  /** Notify Open Operation of Item that might be open in other instances */
  public NotifyForcedOpen(instanceid: string) {
    return this.getKwfHub(false).then(hub => {
      return this.$c.when(hub.server.notifyForcedOpen(instanceid));
    });
  }

  /** Notify a Nav Page was Opened */
  public NotifyNavUiOpened(pageInfo: INavUiPageInfo, _navUrl: TNavUrlSource) {
    const $c = this.$c;
    return $c.when(ResolveNavUrl(_navUrl)).then(navUrl => {
      return this.getKwfHub(false).then(hub => {
        return $c.when(hub.server.notifyNavUiOpened(pageInfo, navUrl.FullUrl));
      });
    });
  }

  // #region KWF Hub Functionality (see Server : KWFHub.cs)

  /** Register Change Notification when OTHER users starts/ends Edit of item
   (Explicit scope.apply might be necessary to reflect changed, as Hub functionality not integrated with angular)
  */
  public RegisterEditChange(callBackOnChange: (ChangedItems: IHubChangeInfo[]) => void) {
    let me = this;
    this.getKwfHub(true).then(undefined, err =>
      console.log("Fehler KWF Hub Initialisierung:" + resolveToError(err)));

    let unsubscribe = this.RwfSignalRService.Register("onEditChange", (infos: IHubChangeInfo[]) => callBackOnChange(infos));

    // TODO : Unregister if no client notifications available?
    if (!this.isEditChangeRegistered) {
      this.isEditChangeRegistered = true;
      me.getKwfHub(true).then(kwfHub => {
        me.GetGlobalSettings().then(s => {
          return me.$c.when(<any>kwfHub.server.registerEditNotifications(s.NavUrl)).then(result => kwfHub);
        });
      });
    }
    return () => {
      if (unsubscribe) {
        unsubscribe.unsubscribe();
        unsubscribe = undefined;
      }
    };
  }
  private isEditChangeRegistered = false;

  /** Re-register edit notifications after SignalR reconnection */
  private reRegisterEditNotifications() {
    if (!this.isEditChangeRegistered) {
      this.isEditChangeRegistered = true;
      const me = this;
      this.getKwfHub(true).then(kwfHub => {
        me.GetGlobalSettings().then(s => {
          console.log("Re-registering edit notifications for NavUrl:", s.NavUrl);
          return me.$c.when(<any>kwfHub.server.registerEditNotifications(s.NavUrl)).then(result => kwfHub);
        });
      });
    }
  }

  /** Register Change Notification when OTHER users starts/ends Edit of item
  (Explicit scope.apply might be necessary to reflect changed, as Hub functionality not integrated with angular)
 */
  public RegisterClientChange(callBackOnChange: (Item: IKWFConnectionInfo, Added: boolean) => void) {
    this.getKwfHub(true).then(undefined, err =>
      console.log("Fehler KWF Hub Initialisierung:" + resolveToError(err)));

    var me = this;
    var registered = (e: { Client: IKWFConnectionInfo, IsAdded: boolean }) => callBackOnChange(e.Client, e.IsAdded);
    me.bind("ClientChange", registered);
    return () => me && me.unbind("ClientChange", registered) && (me = undefined);
  }

  /** Server Synced Application State */
  // MSQQQ -> !!!
  public ApplicationState = new kendo.data.ObservableObject({});
  /** Get (imp Create) KWF Hub, (so far destruction only on WindowUnload) */
  public getKwfHub(forRegister?: boolean): PromiseLike<IKWFHub> {

    const needInitialize = !this._hubInitialised;
    this._hubInitialised = true;

    const signalR = this.RwfSignalRService;
    const result = signalR.getKwfHub();
    if (needInitialize) {
      return this.$c.when(result).then(kwfHub => {
        const subscriptions = [
          signalR.ApplicationState.subscribe(appState => {
            if (appState) {
              //MSQQQ TODO assign local ApplicationState ...
            }
          }),

          signalR.Register("onClientChange", (client: IKWFConnectionInfo, isadd: boolean) => {
            this.trigger("ClientChange", { Client: client, IsAdded: isadd });
          }),
          signalR.Register("onEditedInstanceChanged", (instanceId: string) => {
            this.trigger("EditInstanceChanged", { InstanceId: instanceId });
          }),
          signalR.Register("onPushNotification", (name: string, data: any) => {
            if (data && name) {
              this.ApplicationState.set(name, data);
              this.applicationStateChanged();
            }
          }),
          signalR.Register("onRequestClientState", (serverState: IKWFServerState) => {
            try {
              if (serverState) {
                this.setCurrentServerState(serverState);
              }
            } finally {
              this.getKwfHub().then((kwfHub) => {
                kwfHub.server.pushClientState(this.getApplicationState());
              });
            }
          }),
          signalR.Register("onNavUiClosed", (pageInfo) => {
            this.trigger("NavUiClosed", pageInfo);
          }),
          signalR.Register("onDeleteNotification", name => {
            this.ApplicationState.set(name, undefined);
            this.applicationStateChanged();
          })];

        $(window).on("beforeunload.navHub", () => {
          this.trigger("releaseKWFHub");
          this.RwfSignalRService.releaseKWFHub();
        });
        this.one("releaseKWFHub", () => {
          $(window).off(".navHub");
          subscriptions.forEach(s => s.unsubscribe());
        });

        this.reloadApplicationState(kwfHub);

        return kwfHub;
      });
    }

    return this.$c.when(result);
  }
  private _hubInitialised = false;

  /** Get ServerVersion Info (Including NAV Version for KWF3) */
  public GetServerVersionInfo(forceRefresh: boolean): PromiseLike<AppVersion> {
    if (!forceRefresh) {
      var appState = <IKWFServerState><any>this.ApplicationState;
      var result = (appState && appState.ServerVersion) || <AppVersion>{};
      result.Version = result.Version || "";
      result.PatchLevel = result.PatchLevel || "";
      // Determine NAV Version (possible for KWF3 only!)
      return this.rwfBootstrap.then(s => {
        const kwf3settings = s.RwfSetup;
        result.NavVersion = kwf3settings.Version;
        result.StandaloneInfo = s.RwfSettings.StandaloneInfo;
        const vDate = kwf3settings.VersionDate && kwf3settings.VersionDate.toLocaleDateString ?
          kwf3settings.VersionDate.toLocaleDateString() : <any>kwf3settings.VersionDate;
        result.NavVersionDate = vDate;
        result.NavRelease = kwf3settings.Release;
      }).then(() => fn.clone(result));
    } else {
      return this.getKwfHub(true).then((hub) =>
        this.reloadApplicationState(hub).then(() =>
          this.GetServerVersionInfo(false)));
    }
  }

  /** get Current Application State Information (to Send to Server */
  private getApplicationState(): IKWFClientState {
    var result = this._appState;
    result.CurrentUrl = this.routing.CurrentUrl;
    return result;
  }
  private _appState: IKWFClientState;

  // (Re-)Load Initial ApplicationState
  private reloadApplicationState(kwfHub: IKWFHub) {
    const $c = this.$c;
    return this.$c.when(kwfHub.server.getApplicationState(this.getApplicationState())).then((serverState: IKWFServerState) => {
      // Sync Client Object :
      this.setCurrentServerState(serverState);
      $c.tick();
      return kwfHub;
    });
  }

  /** Refresh AppState Info from Object from Server */
  private setCurrentServerState(serverState: IKWFServerState) {
    var me = this;

    //delete non existing (toJson : ignore kendo.Observable base props)
    Object.getOwnPropertyNames(me.ApplicationState.toJSON()).forEach(name => {
      me.ApplicationState.set(name, serverState[name]);
      delete serverState[name];
    });
    // add new ones
    Object.getOwnPropertyNames(serverState).forEach(name => {
      me.ApplicationState.set(name, serverState[name]);
    });
    this.applicationStateChanged();
  }

  protected applicationStateChanged() {
    return this.GetServerVersionInfo(false).then(serverVersion => {
      this.notifyVersion(this.appVersion, serverVersion);
      this.$c.tick();
    });
  }

  protected abstract notifyVersion(clientVersion: AppVersion, serverVersion: AppVersion): void;
}
